import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal, Stack, Flex, Title, Text, Button } from '@mantine/core';
import { esDiaDeEvento, STAND } from '../../utils/cvdl';
import heroImage from '../../assets/cvdlforvet.png';
import './avisoEvento.css';
import { IconBrandInstagram, IconClick } from '@tabler/icons-react';

const INSTAGRAM = 'https://www.instagram.com/forvetorg/';

// Rutas donde el aviso NO va. La regla es simple: si la pantalla YA es del
// congreso, decirle a alguien "¿estás en el evento? buscanos en el stand" es
// absurdo — está parado ahí. Y en el kiosco además taparía el flujo con un
// modal que nadie del equipo pidió, en una tablet que se opera con guantes.
const EXCLUIDAS = ['/kiosk', '/gemelos', '/gemelo'];

// Una vez por día. La clave lleva la fecha, así que el 24 vuelve a aparecer
// para quien lo cerró el 23 — son tres días distintos de congreso y alguien
// que entró el primero puede no haber ido todavía.
const clave = (dia) => `forvet_aviso_evento_${dia}`;

// El modal del catálogo, en la home, tiene que esperar a que este se cierre.
// En vez de que cada uno adivine lo que hace el otro, este expone su condición
// y avisa cuando termina. Dos modales apilados no los lee nadie.
//
// Es una lista de funciones y NO un CustomEvent sobre `window`: un evento del
// DOM es un objeto con referencias circulares —target, view, currentTarget— y
// cualquier extensión del navegador que escuche el bus global e intente
// serializarlo revienta con "cannot serialize cyclic structures". Los dos
// componentes viven en el mismo bundle: no hay nada que ganar publicándolo.
const suscriptores = new Set();

export function alCerrarseAvisoEvento(fn) {
  suscriptores.add(fn);
  return () => suscriptores.delete(fn);
}

const avisarQueCerro = () => {
  suscriptores.forEach((fn) => {
    try { fn(); } catch { /* un suscriptor roto no arrastra a los demás */ }
  });
};

const hoyEnArgentina = () => {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Argentina/Cordoba',
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(new Date());
  } catch {
    return 'sin-fecha';
  }
};

/**
 * ¿Este aviso va a aparecer ahora? Lo usa la home para saber si tiene que
 * esperar antes de abrir el modal del catálogo. Es la MISMA condición que usa
 * el componente, no una copia: si cambia, cambia para los dos.
 */
export function avisoEventoPendiente() {
  if (!esDiaDeEvento()) return false;
  try {
    return !localStorage.getItem(clave(hoyEnArgentina()));
  } catch {
    return true;   // modo privado: se muestra igual
  }
}

/**
 * Aviso del congreso, solo durante los tres días del evento.
 *
 * Aparece con un respiro de segundo y medio: si se abriera junto con la página
 * competiría con el primer render y se sentiría como un anuncio, no como un
 * aviso. Y nadie lee un modal que aparece antes de haber visto qué hay debajo.
 */
export function AvisoEvento() {
  const { pathname } = useLocation();
  const navegar = useNavigate();
  const [abierto, setAbierto] = useState(false);

  const excluida = EXCLUIDAS.some(r => pathname === r || pathname.startsWith(`${r}/`));

  useEffect(() => {
    if (excluida || !esDiaDeEvento()) return undefined;

    const k = clave(hoyEnArgentina());
    try {
      if (localStorage.getItem(k)) return undefined;
    } catch { /* modo privado: se muestra igual, no se recuerda */ }

    const t = setTimeout(() => setAbierto(true), 1500);
    return () => clearTimeout(t);
  }, [excluida, pathname]);

  function marcarVisto() {
    try { localStorage.setItem(clave(hoyEnArgentina()), '1'); } catch { /* modo privado */ }
  }

  // Sin argumentos a propósito: con `onClick={cerrar}` React le pasa el evento
  // del click, y este proyecto ya se comió una vez ese bug —un MouseEvent
  // llegando como si fuera un parámetro de negocio—. Todos los handlers de
  // abajo la envuelven.
  function cerrar() {
    setAbierto(false);
    marcarVisto();
    avisarQueCerro();   // le suelta el turno al modal del catálogo
  }

  // Instagram abre en otra pestaña. Marcamos visto ya, pero el desmontaje del
 
  function irAInstagram() {
    marcarVisto();
    setTimeout(() => { setAbierto(false); avisarQueCerro(); }, 0);
  }

  function irACasos() {
    cerrar();
    navegar('/casos');
  }

  return (
    <Modal
      opened={abierto}
      onClose={() => cerrar()}
      centered
      radius="lg"
      size='auto'
      withCloseButton
      title={null}
      overlayProps={{ backgroundOpacity: 0.6, blur: 2 }}
      classNames={{ content: 'aviso-evento' }}
      styles={{
        header:{
          backgroundColor:"#fff"
        },
         content:{
          backgroundColor:"#fff"
        }
      }}
    >
      <Stack gap="md" pb="xs">
        {/* La clase lo salva del `img { grayscale(100%) }` global de
            index.css. Sin ella el logo sale gris. */}
        <img src={heroImage} alt="FORVET" className="cvdl-aviso-evento-logo" style={{
          height:'300px',
          // width:'auto',
          objectFit:'contain'
        }} />

        <Stack gap={6} align='center'>
          <Title order={2} style={{color:"#132f35", fontSize:'var(--fs-h2)'}} className="aviso-evento-titulo">
            ¿Estás en el congreso?
          </Title>
          <Text style={{fontSize:"16px", color:"#132f35", textAlign:'center'}} className="aviso-evento-texto">
            Estos días estamos en CVDL Argentina 2026. Pasá por el{' '} <br/>
            <strong style={{fontSize:"16px", color:"#132f35"}}>stand {STAND}</strong> y jugá la ruleta: todos se llevan algo.
          </Text>
          <Text style={{fontSize:"16px", color:"#132f35"}} className="aviso-evento-texto">
            Seguí todo lo que pasa por Instagram.
          </Text>
        </Stack>

        <Flex direction={'column'} gap='sm'>
          <Button
            component="a"
            href={INSTAGRAM}
            leftSection={<IconBrandInstagram/>}
            target="_blank"
            rel="noreferrer"
            onClick={() => irAInstagram()}
          >
            SEGUINOS EN INSTAGRAM
          </Button>
          <Button leftSection={<IconClick/>} variant="outline" color='#132f35' onClick={() => irACasos()}>VER CASOS CLÍNICOS</Button>
          {/* <Button style={{}} variant="subtle" onClick={() => cerrar()}>Aceptar</Button> */}
        </Flex>
      </Stack>
    </Modal>
  );
}
